<script>
import api from '@/helpers/api-rest/api.js';
import { required, email } from 'vuelidate/lib/validators';
import { errorMessages } from '@/helpers/validations/errorMessages';
import { notifyValidationFailedAndFocusFirstInvalid } from '@/helpers/validations/vuelidateFormFeedback';
import Swal from 'sweetalert2';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import AppTextareaAi from '@/components/forms/AppTextareaAi.vue';
import { mapState } from 'vuex';

export default {
    props: {
        // ID del ensayo al que pertenece el centro
        essay_id: {
            type: Number,
            default: null,
        },
        // Tipo de centro (para centro coordinador será 1)
        center_type_id: {
            type: Number,
            default: null,
        },
        // ID del centro a editar. Si es null, el modal funcionará en modo creación
        id: {
            type: [Number, String],
            default: null,
        },
        // ID del modal de Bootstrap (para permitir múltiples instancias)
        modalId: {
            type: String,
            default: 'ModalCenter',
        },
    },
    components: {
        LoadingOverlay,
        AppTextareaAi,
    },
    data() {
        return {
            errorMessages,
            filename: null,
            file: null,
            fileName: null,
            loading: false,
            selectOptionsCourseType: [],
            selectAutonomousCommunityOptions: [],
            selectParticipantTypologyOptions: [],
            // Estado interno del centro (no se recibe ya por props)
            center: {
                id: null,
                center_code: '',
                name: '',
                identification: '',
                director: '',
                address: '',
                city: '',
                province: '',
                cp: '',
                phone: '',
                email: '',
                autonomous_community_id: null,
                participant_typology_id: null,
                sector_subsector: '',
                professional_family: '',
                director_phone: '',
                director_email: '',
                responsible_name: '',
                responsible_phone: '',
                responsible_email: '',
                role: '',
                primary_actions_projects: '',
                modality: null,
                courses: [],
            },
            roleOptions: [
                { value: 'leader', label: 'Líder' },
                { value: 'coordinator', label: 'Coordinador' },
                { value: 'participant', label: 'Participante' }
            ],
            modalityOptions: [
                { value: 1, label: 'Modalidad 1' },
                { value: 2, label: 'Modalidad 2' }
            ],
        };
    },
    computed: {
        ...mapState('loading', {
            globalLoading: (state) => state.loading,
        }),
        modalTitle() {
            return this.center?.id ? 'Editar centro' : 'Añadir centro';
        },
        submitButtonText() {
            return this.center?.id ? 'Guardar' : 'Añadir';
        },
        fileInputLabel() {
            return this.file ? this.file.name : 'Selecciona archivo...';
        }
    },
    created() {
        // La carga de datos se realiza al abrir el modal (@shown)
    },
    methods: {
        loadAutonomousCommunities() {
            const apiEndpoint = `/api/v1/autonomous-communities`;
            api.get({ url: apiEndpoint })
                .then(response => {
                    this.selectAutonomousCommunityOptions = response?.data?.data || [];
                })
                .catch(error => {
                    console.error("Error loading autonomous communities:", error);
                });
        },
        loadCourseTypes() {
            const apiEndpoint = `/api/v1/course-type-shifts`;
            api.get({ url: apiEndpoint })
                .then(response => {
                    this.selectOptionsCourseType = response?.data?.data || [];
                })
                .catch(error => {
                    console.error("Error loading course types:", error);
                });
        },
        loadParticipantTypologies() {
            const apiEndpoint = `/api/v1/participant-typologies`;
            api.get({ url: apiEndpoint })
                .then(response => {
                    this.selectParticipantTypologyOptions = response?.data?.data || [];
                })
                .catch(error => {
                    console.error("Error loading participant typologies:", error);
                });
        },
        /**
         * Se ejecuta cuando el modal se muestra.
         * Carga datos necesarios solo cuando el usuario abre el modal.
         */
        onModalShown() {
            // Cargar catálogos sólo si aún no se han cargado
            if (!this.selectOptionsCourseType.length) {
                this.loadCourseTypes();
            }
            if (!this.selectAutonomousCommunityOptions.length) {
                this.loadAutonomousCommunities();
            }
            if (!this.selectParticipantTypologyOptions.length) {
                this.loadParticipantTypologies();
            }
            // Inicializar/cargar datos del centro (nuevo o edición)
            this.initializeCenter();
        },
        initializeCenter() {
            console.log("initializeCenter", this.id);
            // Si tenemos id, cargamos los datos frescos desde la API
            if (this.id) {
                this.loadCenterById(this.id);
                return;
            }
            // Si no hay id, nos aseguramos de tener un objeto centro limpio
            this.resetCenter();
        },
        resetCenter() {
            // Inicializa todos los campos del centro
            this.center = {
                id: null,
                center_code: '',
                name: '',
                identification: '',
                director: '',
                address: '',
                city: '',
                province: '',
                cp: '',
                phone: '',
                email: '',
                autonomous_community_id: null,
                participant_typology_id: null,
                sector_subsector: '',
                professional_family: '',
                director_phone: '',
                director_email: '',
                responsible_name: '',
                responsible_phone: '',
                responsible_email: '',
                role: '',
                primary_actions_projects: '',
                modality: null,
                courses: [],
            };
        },
        loadCenterById(id) {
            const apiEndpoint = `/api/v1/centers/${id}`;
            this.loading = true;
            console.log("loadCenterById", id);
            api.get({ url: apiEndpoint })
                .then(response => {
                    this.loading = false;
                    const data = response?.data?.data || response?.data || {};
                    this.center = data;
                })
                .catch(error => {
                    this.loading = false;
                    console.error("Error loading center:", error);
                });
        },
        createCenter() {
            this.loading = true;
            const apiEndpoint = `/api/v1/centers`;
            
            // Prepare center data with essay_id and center_type_id
            const centerData = {
                ...this.center,
                essay_id: this.essay_id,
                center_type_id: this.center_type_id,
            };
            
            api.post({ url: apiEndpoint, data: centerData })
                .then(response => {
                    this.loading = false;
                    const newCenter = response?.data?.data || response?.data;
                    this.$emit('success', newCenter);
                    this.$bvModal.hide(this.modalId);
                })
                .catch(error => {
                    this.loading = false;
                    console.error("Error al crear el centro:", error);
                    
                    // Extract error message from response
                    const errorMessage =
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        "Error al crear el centro. Por favor, inténtelo de nuevo.";
                    
                    Swal.fire({
                        icon: "error",
                        title: "Error al crear el centro",
                        html: errorMessage,
                        confirmButtonColor: "#34c38f",
                        confirmButtonText: "Aceptar",
                    });
                });
        },
        updateCenter() {
            this.loading = true;
            const apiEndpoint = `/api/v1/centers`;
            
            // Prepare center data with essay_id and center_type_id
            const centerData = {
                ...this.center,
                essay_id: this.essay_id,
                center_type_id: this.center_type_id,
            };
            
            api.put({ url: apiEndpoint, data: centerData, id: this.center.id })
                .then(response => {
                    this.loading = false;
                    const updatedCenter = response?.data?.data || response?.data;
                    this.$emit('success', updatedCenter);
                    this.$bvModal.hide(this.modalId);
                })
                .catch(error => {
                    this.loading = false;
                    console.error("Error editing center:", error);
                    
                    // Extract error message from response
                    const errorMessage =
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        error?.message ||
                        "Error al actualizar el centro. Por favor, inténtelo de nuevo.";
                    
                    Swal.fire({
                        icon: "error",
                        title: "Error al actualizar el centro",
                        html: errorMessage,
                        confirmButtonColor: "#34c38f",
                        confirmButtonText: "Aceptar",
                    });
                });
        },
        submitForm() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                notifyValidationFailedAndFocusFirstInvalid(this);
                return;
            }

            if (this.center?.id) {
                return this.updateCenter();
            }
            return this.createCenter();
        },
        cancelForm() {
            this.$v.$reset();
            this.$bvModal.hide(this.modalId);
            this.$emit('close-modal');
        },
        addRow() {
            this.center?.courses.push({
                mode_code: '',
                education: '',
                mode: '',
                shift_id: '',
            });
        },
        deleteRow(key) {
            this.center?.courses.splice(key, 1);
        },
    },
    validations: {
        center: {
            name: { required },
            center_code: { required },
            identification: { required },
            autonomous_community_id: { required },
            participant_typology_id: { required },
            sector_subsector: { required },
            professional_family: { required },
            email: { required, email },
            phone: { required },
            role: { required },
            director: { required },
            director_phone: { required },
            director_email: { required, email },
            responsible_name: { required },
            responsible_phone: { required },
            responsible_email: { required, email },
            primary_actions_projects: { required },
            modality: { required },
            address: { required },
            city: { required },
            province: { required },
            cp: { required },
            courses: {
                $each: {
                    mode_code: { required },
                    education: { required },
                    mode: { required },
                    shift_id: { required },
                },
            },
        }
    }
}
</script>
<template>
    <b-modal
      :id="modalId"
      :title="modalTitle"
      size="xl"
      no-close-on-backdrop
      no-close-on-esc
      scrollable
      @shown="onModalShown"
    >  
      <template #default="{  }">
        <div class="position-relative">
          <loading-overlay :show="globalLoading" />
          <form @submit.prevent="submitForm" enctype="multipart/form-data">
            <!-- Section 1: Información Básica del Centro -->
            <h5 class="card-title mb-3">Información Básica del Centro</h5>
            <div class="row">
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="name">Nombre del centro <span style="color: red">*</span></label>
                        <input 
                            v-model="center.name" 
                            type="text" 
                            class="form-control" 
                            id="name"
                            maxlength="200"
                            :class="{ 'is-invalid': $v.center.name.$error }"
                        >
                        <small class="text-muted">{{ center?.name?.length || 0 }}/200</small>
                        <div v-if="$v.center.name.$error" class="invalid-feedback">
                            <div v-if="!$v.center.name.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="center_code">Código del centro <span style="color: red">*</span></label>
                        <input 
                            v-model="center.center_code" 
                            type="text" 
                            class="form-control" 
                            id="center_code"
                            maxlength="50"
                            :class="{ 'is-invalid': $v.center.center_code.$error }"
                        >
                        <small class="text-muted">{{ center?.center_code?.length || 0 }}/50</small>
                        <div v-if="$v.center.center_code.$error" class="invalid-feedback">
                            <div v-if="!$v.center.center_code.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="identification">NIF/CIF <span style="color: red">*</span></label>
                        <input 
                            v-model="center.identification" 
                            type="text" 
                            class="form-control" 
                            id="identification"
                            maxlength="20"
                            :class="{ 'is-invalid': $v.center.identification.$error }"
                        >
                        <small class="text-muted">{{ center?.identification?.length || 0 }}/20</small>
                        <div v-if="$v.center.identification.$error" class="invalid-feedback">
                            <div v-if="!$v.center.identification.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="autonomous_community_id">Comunidad autónoma <span style="color: red">*</span></label>
                        <select 
                            v-model="center.autonomous_community_id" 
                            class="form-control" 
                            id="autonomous_community_id"
                            :class="{ 'is-invalid': $v.center.autonomous_community_id.$error }"
                        >
                            <option :value="null">Selecciona comunidad autónoma</option>
                            <option 
                                v-for="option in selectAutonomousCommunityOptions" 
                                :value="option.id" 
                                :key="option.id"
                            >
                                {{ option.name }}
                            </option>
                        </select>
                        <div v-if="$v.center.autonomous_community_id.$error" class="invalid-feedback">
                            <div v-if="!$v.center.autonomous_community_id.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="participant_typology_id">Tipología participante <span style="color: red">*</span></label>
                        <select 
                            v-model="center.participant_typology_id" 
                            class="form-control" 
                            id="participant_typology_id"
                            :class="{ 'is-invalid': $v.center.participant_typology_id.$error }"
                        >
                            <option :value="null">Selecciona tipología participante</option>
                            <option 
                                v-for="option in selectParticipantTypologyOptions" 
                                :value="option.id" 
                                :key="option.id"
                            >
                                {{ option.name }}
                            </option>
                        </select>
                        <div v-if="$v.center.participant_typology_id.$error" class="invalid-feedback">
                            <div v-if="!$v.center.participant_typology_id.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="role">Rol <span style="color: red">*</span></label>
                        <select 
                            v-model="center.role" 
                            class="form-control" 
                            id="role"
                            :class="{ 'is-invalid': $v.center.role.$error }"
                        >
                            <option value="">Selecciona rol</option>
                            <option 
                                v-for="option in roleOptions" 
                                :value="option.value" 
                                :key="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                        <div v-if="$v.center.role.$error" class="invalid-feedback">
                            <div v-if="!$v.center.role.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="sector_subsector">Sector/subsector <span style="color: red">*</span></label>
                        <input 
                            v-model="center.sector_subsector" 
                            type="text" 
                            class="form-control" 
                            id="sector_subsector"
                            maxlength="200"
                            :class="{ 'is-invalid': $v.center.sector_subsector.$error }"
                        >
                        <small class="text-muted">{{ center?.sector_subsector?.length || 0 }}/200</small>
                        <div v-if="$v.center.sector_subsector.$error" class="invalid-feedback">
                            <div v-if="!$v.center.sector_subsector.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="professional_family">Familia profesional <span style="color: red">*</span></label>
                        <input 
                            v-model="center.professional_family" 
                            type="text" 
                            class="form-control" 
                            id="professional_family"
                            maxlength="200"
                            :class="{ 'is-invalid': $v.center.professional_family.$error }"
                        >
                        <small class="text-muted">{{ center?.professional_family?.length || 0 }}/200</small>
                        <div v-if="$v.center.professional_family.$error" class="invalid-feedback">
                            <div v-if="!$v.center.professional_family.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <hr style="border-top: 2px solid rgb(0 0 0 / 10%); margin: 20px 0;" />

            <!-- Section 2: Datos de Contacto del Centro -->
            <h5 class="card-title mb-3">Datos de Contacto del Centro</h5>
            <div class="form-group">
                <label for="address">Dirección <span style="color: red">*</span></label>
                <input 
                    v-model="center.address" 
                    type="text" 
                    class="form-control" 
                    id="address"
                    maxlength="500"
                    :class="{ 'is-invalid': $v.center.address.$error }"
                >
                <small class="text-muted">{{ center?.address?.length || 0 }}/500</small>
                <div v-if="$v.center.address.$error" class="invalid-feedback">
                    <div v-if="!$v.center.address.required">{{ errorMessages.required }}</div>
                </div>
            </div>
            <div class="row">
                <div class="col-lg-4">
                    <div class="form-group">
                        <label for="city">Ciudad <span style="color: red">*</span></label>
                        <input 
                            v-model="center.city" 
                            type="text" 
                            class="form-control" 
                            id="city"
                            maxlength="100"
                            :class="{ 'is-invalid': $v.center.city.$error }"
                        >
                        <small class="text-muted">{{ center?.city?.length || 0 }}/100</small>
                        <div v-if="$v.center.city.$error" class="invalid-feedback">
                            <div v-if="!$v.center.city.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-4">
                    <div class="form-group">
                        <label for="province">Provincia <span style="color: red">*</span></label>
                        <input 
                            v-model="center.province" 
                            type="text" 
                            class="form-control" 
                            id="province"
                            maxlength="100"
                            :class="{ 'is-invalid': $v.center.province.$error }"
                        >
                        <small class="text-muted">{{ center?.province?.length || 0 }}/100</small>
                        <div v-if="$v.center.province.$error" class="invalid-feedback">
                            <div v-if="!$v.center.province.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-4">
                    <div class="form-group">
                        <label for="cp">Código postal <span style="color: red">*</span></label>
                        <input 
                            v-model="center.cp" 
                            type="text" 
                            class="form-control" 
                            id="cp"
                            maxlength="10"
                            :class="{ 'is-invalid': $v.center.cp.$error }"
                        >
                        <small class="text-muted">{{ center?.cp?.length || 0 }}/10</small>
                        <div v-if="$v.center.cp.$error" class="invalid-feedback">
                            <div v-if="!$v.center.cp.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="phone">Teléfono <span style="color: red">*</span></label>
                        <input 
                            v-model="center.phone" 
                            type="text" 
                            class="form-control" 
                            id="phone"
                            maxlength="20"
                            :class="{ 'is-invalid': $v.center.phone.$error }"
                        >
                        <small class="text-muted">{{ center?.phone?.length || 0 }}/20</small>
                        <div v-if="$v.center.phone.$error" class="invalid-feedback">
                            <div v-if="!$v.center.phone.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="email">Email <span style="color: red">*</span></label>
                        <input 
                            v-model="center.email" 
                            type="email" 
                            class="form-control" 
                            id="email"
                            maxlength="255"
                            :class="{ 'is-invalid': $v.center.email.$error }"
                        >
                        <small class="text-muted">{{ center?.email?.length || 0 }}/255</small>
                        <div v-if="$v.center.email.$error" class="invalid-feedback">
                            <div v-if="!$v.center.email.required">{{ errorMessages.required }}</div>
                            <div v-else-if="!$v.center.email.email">{{ errorMessages.email }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <hr style="border-top: 2px solid rgb(0 0 0 / 10%); margin: 20px 0;" />

            <!-- Section 3: Datos del Director -->
            <h5 class="card-title mb-3">Datos de Dirección</h5>
            <div class="row">
                <div class="col-lg-4">
                    <div class="form-group">
                        <label for="director">Nombre del director/a <span style="color: red">*</span></label>
                        <input 
                            v-model="center.director" 
                            type="text" 
                            class="form-control" 
                            id="director"
                            maxlength="200"
                            :class="{ 'is-invalid': $v.center.director.$error }"
                        >
                        <small class="text-muted">{{ center?.director?.length || 0 }}/200</small>
                        <div v-if="$v.center.director.$error" class="invalid-feedback">
                            <div v-if="!$v.center.director.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-4">
                    <div class="form-group">
                        <label for="director_phone">Teléfono del director/a <span style="color: red">*</span></label>
                        <input 
                            v-model="center.director_phone" 
                            type="text" 
                            class="form-control" 
                            id="director_phone"
                            maxlength="20"
                            :class="{ 'is-invalid': $v.center.director_phone.$error }"
                        >
                        <small class="text-muted">{{ center?.director_phone?.length || 0 }}/20</small>
                        <div v-if="$v.center.director_phone.$error" class="invalid-feedback">
                            <div v-if="!$v.center.director_phone.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-4">
                    <div class="form-group">
                        <label for="director_email">Email del director/a <span style="color: red">*</span></label>
                        <input 
                            v-model="center.director_email" 
                            type="email" 
                            class="form-control" 
                            id="director_email"
                            maxlength="255"
                            :class="{ 'is-invalid': $v.center.director_email.$error }"
                        >
                        <small class="text-muted">{{ center?.director_email?.length || 0 }}/255</small>
                        <div v-if="$v.center.director_email.$error" class="invalid-feedback">
                            <div v-if="!$v.center.director_email.required">{{ errorMessages.required }}</div>
                            <div v-else-if="!$v.center.director_email.email">{{ errorMessages.email }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <hr style="border-top: 2px solid rgb(0 0 0 / 10%); margin: 20px 0;" />

            <!-- Section 4: Datos del Responsable -->
            <h5 class="card-title mb-3">Datos Responsable</h5>
            <div class="row">
                <div class="col-lg-4">
                    <div class="form-group">
                        <label for="responsible_name">Nombre responsable <span style="color: red">*</span></label>
                        <input 
                            v-model="center.responsible_name" 
                            type="text" 
                            class="form-control" 
                            id="responsible_name"
                            maxlength="200"
                            :class="{ 'is-invalid': $v.center.responsible_name.$error }"
                        >
                        <small class="text-muted">{{ center?.responsible_name?.length || 0 }}/200</small>
                        <div v-if="$v.center.responsible_name.$error" class="invalid-feedback">
                            <div v-if="!$v.center.responsible_name.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-4">
                    <div class="form-group">
                        <label for="responsible_phone">Teléfono responsable <span style="color: red">*</span></label>
                        <input 
                            v-model="center.responsible_phone" 
                            type="text" 
                            class="form-control" 
                            id="responsible_phone"
                            maxlength="20"
                            :class="{ 'is-invalid': $v.center.responsible_phone.$error }"
                        >
                        <small class="text-muted">{{ center?.responsible_phone?.length || 0 }}/20</small>
                        <div v-if="$v.center.responsible_phone.$error" class="invalid-feedback">
                            <div v-if="!$v.center.responsible_phone.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-4">
                    <div class="form-group">
                        <label for="responsible_email">Email responsable <span style="color: red">*</span></label>
                        <input 
                            v-model="center.responsible_email" 
                            type="email" 
                            class="form-control" 
                            id="responsible_email"
                            maxlength="255"
                            :class="{ 'is-invalid': $v.center.responsible_email.$error }"
                        >
                        <small class="text-muted">{{ center?.responsible_email?.length || 0 }}/255</small>
                        <div v-if="$v.center.responsible_email.$error" class="invalid-feedback">
                            <div v-if="!$v.center.responsible_email.required">{{ errorMessages.required }}</div>
                            <div v-else-if="!$v.center.responsible_email.email">{{ errorMessages.email }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <hr style="border-top: 2px solid rgb(0 0 0 / 10%); margin: 20px 0;" />

            <!-- Section 5: Información Adicional -->
            <h5 class="card-title mb-3">Información Adicional</h5>
            <div class="form-group">
                <label for="primary_actions_projects">Actuaciones / proyectos primarios <span style="color: red">*</span></label>
                <app-textarea-ai
                    v-model="center.primary_actions_projects"
                    :enable-ai="true"
                    :max-chars="5000"
                    :rows="4"
                    placeholder="Actuaciones / proyectos primarios"
                    :textarea-class="{
                        'form-control': true,
                        'is-invalid': $v.center.primary_actions_projects.$error,
                    }"
                    id="primary_actions_projects"
                ></app-textarea-ai>
                <small class="text-muted">{{ center?.primary_actions_projects?.length || 0 }}/5000</small>
                <div v-if="$v.center.primary_actions_projects.$error" class="invalid-feedback">
                    <div v-if="!$v.center.primary_actions_projects.required">{{ errorMessages.required }}</div>
                </div>
            </div>
            <div class="row">
                <div class="col-lg-6">
                    <div class="form-group">
                        <label for="modality">Modalidad de convocatoria <span style="color: red">*</span></label>
                        <select 
                            v-model="center.modality" 
                            class="form-control" 
                            id="modality"
                            :class="{ 'is-invalid': $v.center.modality.$error }"
                        >
                            <option :value="null">Selecciona modalidad</option>
                            <option 
                                v-for="option in modalityOptions" 
                                :value="option.value" 
                                :key="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                        <div v-if="$v.center.modality.$error" class="invalid-feedback">
                            <div v-if="!$v.center.modality.required">{{ errorMessages.required }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <hr style="border-top: 2px solid rgb(0 0 0 / 10%); margin: 20px 0;" />

            <!-- Section 6: Ciclos Formativos -->
            <h5 class="card-title mb-3">Ciclos Formativos Relacionados con el Proyecto</h5>
            <div class="form-group col-12">
                <table class="table table-bordered table-sticky-header">
                    <thead>
                        <tr>
                            <th scope="col" colspan="5">
                                Ciclos 
                                <a href="javascript:void(0);" class="text-success float-right" @click="addRow()">
                                    <i class="mdi mdi-plus-circle font-size-18"></i> Agregar ciclo
                                </a>
                            </th>
                        </tr>
                        <tr>
                            <th scope="col">
                                Código <span style="color: red">*</span>
                                <i
                                    v-b-tooltip.hover
                                    title="Ejemplo: FPB124"
                                    class="far fa-question-circle font-size-12 ml-2"
                                ></i>
                            </th>
                            <th scope="col">
                                Enseñanza <span style="color: red">*</span>
                                <i
                                    v-b-tooltip.hover
                                    title="Ejemplo: Ciclos Formativos de Formación Profesional de Grado Básico"
                                    class="far fa-question-circle font-size-12 ml-2"
                                ></i>
                            </th>
                            <th scope="col">
                                Modalidad <span style="color: red">*</span>
                                <i
                                    v-b-tooltip.hover
                                    title="Ejemplo: Profesional básico en mantenimiento de viviendas"
                                    class="far fa-question-circle font-size-12 ml-2"
                                ></i>
                            </th>
                            <th scope="col">
                                Tipo de curso <span style="color: red">*</span>
                                <i
                                    v-b-tooltip.hover
                                    title="Ejemplo: Diurno"
                                    class="far fa-question-circle font-size-12 ml-2"
                                ></i>
                            </th>
                            <th scope="col" class="text-center" style="width: 80px;">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(course, i) in center.courses" :key="i">
                            <td>
                                <div class="form-group mb-0">
                                    <input
                                        v-model="course.mode_code"
                                        type="text"
                                        class="form-control"
                                        :id="`course_mode_code_${i}`"
                                        placeholder="FPB124"
                                        maxlength="50"
                                        :class="{
                                            'is-invalid':
                                                $v.center.courses.$each[i] &&
                                                $v.center.courses.$each[i].mode_code.$error,
                                        }"
                                    />
                                    <div
                                        v-if="
                                            $v.center.courses.$each[i] &&
                                            $v.center.courses.$each[i].mode_code.$error
                                        "
                                        class="invalid-feedback d-block"
                                    >
                                        <span v-if="!$v.center.courses.$each[i].mode_code.required">{{
                                            errorMessages.required
                                        }}</span>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div class="form-group mb-0">
                                    <input
                                        v-model="course.education"
                                        type="text"
                                        class="form-control"
                                        :id="`course_education_${i}`"
                                        placeholder="Ciclos Formativos de Formación Profesional de Grado Básico"
                                        maxlength="200"
                                        :class="{
                                            'is-invalid':
                                                $v.center.courses.$each[i] &&
                                                $v.center.courses.$each[i].education.$error,
                                        }"
                                    />
                                    <div
                                        v-if="
                                            $v.center.courses.$each[i] &&
                                            $v.center.courses.$each[i].education.$error
                                        "
                                        class="invalid-feedback d-block"
                                    >
                                        <span v-if="!$v.center.courses.$each[i].education.required">{{
                                            errorMessages.required
                                        }}</span>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div class="form-group mb-0">
                                    <input
                                        v-model="course.mode"
                                        type="text"
                                        class="form-control"
                                        :id="`course_mode_${i}`"
                                        placeholder="Profesional básico en mantenimiento de viviendas"
                                        maxlength="100"
                                        :class="{
                                            'is-invalid':
                                                $v.center.courses.$each[i] &&
                                                $v.center.courses.$each[i].mode.$error,
                                        }"
                                    />
                                    <div
                                        v-if="
                                            $v.center.courses.$each[i] &&
                                            $v.center.courses.$each[i].mode.$error
                                        "
                                        class="invalid-feedback d-block"
                                    >
                                        <span v-if="!$v.center.courses.$each[i].mode.required">{{
                                            errorMessages.required
                                        }}</span>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div class="form-group mb-0">
                                    <select
                                        v-model="course.shift_id"
                                        class="form-control"
                                        :id="`course_shift_${i}`"
                                        :class="{
                                            'is-invalid':
                                                $v.center.courses.$each[i] &&
                                                $v.center.courses.$each[i].shift_id.$error,
                                        }"
                                    >
                                        <option value="">Selecciona tipo</option>
                                        <option
                                            v-for="option in selectOptionsCourseType"
                                            :value="option.id"
                                            :key="option.id"
                                        >
                                            {{ option.name }}
                                        </option>
                                    </select>
                                    <div
                                        v-if="
                                            $v.center.courses.$each[i] &&
                                            $v.center.courses.$each[i].shift_id.$error
                                        "
                                        class="invalid-feedback d-block"
                                    >
                                        <span v-if="!$v.center.courses.$each[i].shift_id.required">{{
                                            errorMessages.required
                                        }}</span>
                                    </div>
                                </div>
                            </td>
                            <td class="text-center">
                                <a
                                    href="javascript:void(0);"
                                    class="text-danger"
                                    v-b-tooltip.hover
                                    title="Eliminar"
                                    @click="deleteRow(i)"
                                >
                                    <i class="mdi mdi-trash-can font-size-18"></i>
                                </a>
                            </td>
                        </tr>
                        <tr v-if="center.courses.length === 0">
                            <td colspan="5" class="text-center text-muted">
                                No hay ciclos formativos añadidos. Haz clic en "Agregar ciclo" para añadir uno.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
          </form>
        </div>
      </template>
  
      <template #modal-footer="{  }">
        <b-button
          variant="primary"
          @click="submitForm()"
          :disabled="loading || globalLoading"
        >
          <span
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
            v-if="loading || globalLoading"
          ></span>
          {{ submitButtonText }}
        </b-button>
        <b-button @click="cancelForm()" :disabled="loading || globalLoading">
          Cancelar
        </b-button>
      </template>
    </b-modal>
</template>
